import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { ZfBox, ZfInput, ZfSelectOption, ZfTypography, ZfSearchOption } from '@zafire/react';
import { GlobalState } from 'reducers/stateTypes';

/**
 * AgGrid Custom Filter Props Interface
 */
interface ICreatorColumnFilterProps {
  filterChangedCallback?: () => void;
  api?: any;
  columnApi?: any;
  column?: any;
  registerFilter?: (filterInstance: any) => void;
}

/**
 * Custom AgGrid column filter for Partner selection
 * Uses PSH API for partner search (same as creation screen)
 * Provides server-side searchable dropdown with debounced search
 */

const DROPDOWN_HEIGHT = 48; // Increased height for detailed labels with DOI and Incorporation

const CreatorFilterList = [
  { label: 'Equals', value: 'equals' },
  { label: 'Contains', value: 'contains' },
];
export const CreatorColumnFilter = forwardRef<any, ICreatorColumnFilterProps>((props, ref) => {
  const [condition, setCondition] = useState<string>('equals');
  const [selectedCreator, setSelectedCreator] = useState<string>('');
  const [employeeSuggestion, setEmployeeSuggestion] = useState('');
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);

  const employeeFilteredOptions = useMemo(() => {
    const suggestion = employeeSuggestion.toLowerCase();
    const data = employeeOptions.filter((option) => option?.label?.toLowerCase().includes(suggestion));
    return data?.length > 50 ? data.slice(0, 50) : data;
  }, [employeeOptions, employeeSuggestion]);

  const { t } = useTranslation();

  // Register filter instance with parent component
  useEffect(() => {
    if (props.registerFilter) {
      // Pass the current component instance (forwardRef creates a ref object)
      props.registerFilter({
        setModel: (model: any) => {
          if (model === null || model === undefined) {
            setSelectedCreator('');
            setEmployeeSuggestion('');
          }
        },
      });
    }
  }, [props]);

  // AgGrid filter interface methods
  useImperativeHandle(ref, () => {
    return {
      // Called by AgGrid when it needs to know if filter is active
      isFilterActive() {
        return selectedCreator !== null && selectedCreator !== '';
      },

      // Called by AgGrid to get the filter model
      getModel() {
        if (selectedCreator === '' || selectedCreator === null) {
          return null;
        }
        return {
          filterType: 'text',
          type: 'equals',
          filter: selectedCreator, // Partner UUID
        };
      },

      // Called by AgGrid to set the filter model (e.g., on filter restore or when reopening)
      setModel(model: any) {
        if (model === null || model === undefined) {
          setSelectedCreator('');
          setEmployeeSuggestion('');
        } else {
          // Restore selected partner from model
          if (model.filter) {
            // Try to find partner in current list
            const creator = employeeOptions?.find((p) => p.value === model.filter);
            if (creator) {
              setSelectedCreator(creator.value);
            } else {
              setSelectedCreator('');
            }
          }
        }
      },

      // AgGrid calls this to see if component implements custom methods
      doesFilterPass() {
        return true;
      },
    };
  }, [selectedCreator, employeeOptions]);

  const updateFilter = useCallback(() => {
    if (props.api && props.column) {
      try {
        // Get current filter model
        const currentFilterModel = props.api.getFilterModel() || {};

        // Build new filter model for this column
        const columnId = props.column.getColId();
        const newFilterModel = { ...currentFilterModel };

        if (selectedCreator) {
          // Set filter for this column
          newFilterModel[columnId] = {
            filterType: 'text',
            type: condition,
            filter: selectedCreator,
          };
        } else {
          // Remove filter for this column
          delete newFilterModel[columnId];
        }

        // Apply the new filter model
        props.api.setFilterModel(newFilterModel);

        // Now trigger the refresh
        if (props.api.refreshServerSide) {
          props.api.refreshServerSide({ purge: true });
        } else if (props.api.onFilterChanged) {
          props.api.onFilterChanged();
        }
        if (props.api.hideColumnFilter) {
          props.api.hideColumnFilter();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[CreatorColumnFilter] Error setting filter model:', error);
      }
    }
  }, [props.api, props.column, selectedCreator, condition]);

  const resetFilter = useCallback(() => {
    if (props.api && props.column) {
      try {
        // Get current filter model
        const currentFilterModel = props.api.getFilterModel() || {};

        // Build new filter model for this column
        const columnId = props.column.getColId();
        const newFilterModel = { ...currentFilterModel };

        // Remove filter for this column
        delete newFilterModel[columnId];

        // Apply the new filter model
        props.api.setFilterModel(newFilterModel);

        // Now trigger the refresh
        if (props.api.refreshServerSide) {
          props.api.refreshServerSide({ purge: true });
        } else if (props.api.onFilterChanged) {
          props.api.onFilterChanged();
        }
        if (props.api.hideColumnFilter) {
          props.api.hideColumnFilter();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[CreatoreFilter] Error setting filter model:', error);
      }
    }
  }, [props.api, props.column]);

  return (
    <div className="ag-filter-wrapper" style={{ width: '100%' }}>
      <div className="ag-filter-body-wrapper ag-simple-filter-body-wrapper ag-focus-managed">
        <ZfInput
          type="select"
          onSelectChange={(e) => setCondition(e.detail as string)}
          value={condition}
          getOptionLabel={(co) => CreatorFilterList.find((c) => c.value === co)?.label || ''}
        >
          {CreatorFilterList.map((co) => {
            return (
              <ZfSelectOption key={co.value} value={co.value} isSelected={condition === co.value}>
                {co.label}
              </ZfSelectOption>
            );
          })}
        </ZfInput>
      </div>
      <ZfBox
        styles={{
          paddingLeft: 'var(--gap-horizontal-sm)',
          paddingRight: 'var(--gap-horizontal-sm)',
          paddingBottom: 'var(--gap-vertical-sm)',
        }}
      >
        <ZfInput
          name="selectedCreator"
          type="search"
          onSearchSubmit={(e) => {
            setSelectedCreator(e.detail as string);
          }}
          onInputChange={(e) => {
            setEmployeeSuggestion(e.detail);
            setSelectedCreator(e.detail);
          }}
          placeholder={t('contactNote.home.filters.placeholder.search')}
          allowSearch
          value={selectedCreator}
          getOptionLabel={(option) =>
            employeeOptions.find((co) => co.value === option.toLowerCase())?.label || `N/A (${option})`
          }
        >
          {employeeSuggestion === '' && (
            <ZfBox
              style={{
                padding: 'var(--gap-vertical-xs)',
                paddingBottom: 'var(--gap-vertical-xs)',
                paddingLeft: 'var(--gap-horizontal-xs)',
              }}
            >
              <ZfTypography variant="label-small" color="normal">
                {t('contactNote.form.startSearching')}
              </ZfTypography>
            </ZfBox>
          )}
          {employeeSuggestion !== '' && employeeFilteredOptions.length === 0 && (
            <ZfBox
              style={{
                padding: 'var(--gap-vertical-xs)',
                paddingBottom: 'var(--gap-vertical-xs)',
                paddingLeft: 'var(--gap-horizontal-md)',
              }}
            >
              <ZfTypography variant="body-small" color="normal">
                {t('contactNote.form.searchFail')}
              </ZfTypography>
            </ZfBox>
          )}
          {employeeFilteredOptions.length > 0 && (
            <FixedSizeList
              itemSize={DROPDOWN_HEIGHT}
              itemCount={employeeFilteredOptions.length}
              overscanCount={5}
              height={Math.min(employeeFilteredOptions.length, 6) * DROPDOWN_HEIGHT}
              width="100%"
            >
              {(listProps: ListChildComponentProps) => {
                const { index, style } = listProps;
                const co = employeeFilteredOptions[index];
                return (
                  <ZfSearchOption
                    style={style}
                    titleText={co.name}
                    titleHighlight={selectedCreator}
                    description={co.value}
                    descriptionHighlight={selectedCreator}
                    key={co.value}
                  />
                );
              }}
            </FixedSizeList>
          )}
        </ZfInput>
      </ZfBox>
      <div className="ag-filter-apply-panel">
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button ag-filter-apply-panel-apply-button"
          data-ref="applyFilterButton"
          type="submit"
          tabIndex={0}
          onClick={() => {
            updateFilter();
          }}
        >
          Apply
        </button>
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          data-ref="resetFilterButton"
          type="button"
          tabIndex={0}
          onClick={() => {
            setSelectedCreator('');
            setEmployeeSuggestion('');
            resetFilter();
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
});

CreatorColumnFilter.displayName = 'CreatorColumnFilter';

export default CreatorColumnFilter;
